import React from 'react';

const AboutUs = () => {
  return (
    <div className="mt-20 pt-20 pb-16 bg-amber-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <h1 className="text-5xl md:text-6xl font-elegant font-bold text-black mb-6 fade-in-up">
            About Svaryn
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed fade-in-up delay-200">
            Crafting timeless silver jewellery pieces that tell your unique story. 
            Each piece is handcrafted with love and attention to detail.
          </p>
        </div>

        {/* Our Story Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <div className="fade-in-left">
            <h2 className="text-3xl font-elegant font-bold text-black mb-6">Our Story</h2>
            <p className="text-gray-600 mb-4 leading-relaxed">
              Founded in 2024, Svaryn Jewellery began as a small family workshop in the heart of Hyderabad, India. 
              What started as a passion project has grown into a beloved brand known for its exceptional craftsmanship 
              and innovative fusion of traditional and modern designs.
            </p>
            <p className="text-gray-600 mb-4 leading-relaxed">
              Our founder, Gautham Talloju, inherited his family's deep-rooted love for traditional Indian jewellery 
              and age-old crafting techniques. Growing up in a family that has been in the jewellery business for 
              generations, Gautham learned the art of metalwork and gemstone setting from his grandfather.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Today, Svaryn continues to honor those traditional techniques while embracing contemporary innovation, 
              creating jewellery that seamlessly blends classic Indian heritage with modern elegance, making it 
              perfect for both traditional occasions and everyday wear.
            </p>
          </div>
          <div className="bg-amber-100 rounded-2xl p-8 border border-amber-200 hover-lift fade-in-right">
            <div className="w-24 h-24 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-6 scale-in">
              <svg className="w-12 h-12 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-2xl font-semibold text-black mb-4 text-center">Our Heritage</h3>
            <p className="text-gray-600 text-center">
              Rooted in Indian tradition, inspired by modern innovation, and driven by passion for creating 
              jewellery that bridges the gap between heritage and contemporary style.
            </p>
          </div>
        </div>

        {/* Mission & Values */}
        <div className="mb-20">
          <h2 className="text-3xl font-elegant font-bold text-black text-center mb-12 fade-in-up">Our Mission & Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-amber-100 rounded-xl p-8 border border-amber-200 text-center hover-lift fade-in-left delay-300">
              <div className="w-16 h-16 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-4 scale-in">
                <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-black mb-3">Craftsmanship</h3>
              <p className="text-gray-600">
                Every piece is handcrafted by skilled artisans using traditional Indian techniques 
                passed down through generations, combined with modern precision tools.
              </p>
            </div>

            <div className="bg-amber-100 rounded-xl p-8 border border-amber-200 text-center hover-lift fade-in-up delay-400">
              <div className="w-16 h-16 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-4 scale-in">
                <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-black mb-3">Fusion Design</h3>
              <p className="text-gray-600">
                We blend traditional Indian jewellery aesthetics with contemporary design elements, 
                creating pieces that honor heritage while embracing modern trends.
              </p>
            </div>

            <div className="bg-amber-100 rounded-xl p-8 border border-amber-200 text-center hover-lift fade-in-right delay-500">
              <div className="w-16 h-16 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-4 scale-in">
                <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-black mb-3">Heritage</h3>
              <p className="text-gray-600">
                We believe in preserving and celebrating Indian jewellery traditions while making them 
                accessible and relevant for today's modern lifestyle.
              </p>
            </div>
          </div>
        </div>

        {/* Our Process */}
        <div className="mb-20">
          <h2 className="text-3xl font-elegant font-bold text-black text-center mb-12 fade-in-up">How We Create</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center stagger-item" style={{ animationDelay: '0.1s' }}>
              <div className="w-20 h-20 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-4 scale-in">
                <span className="text-2xl font-bold text-black">1</span>
              </div>
              <h3 className="text-lg font-semibold text-black mb-2">Inspiration</h3>
              <p className="text-gray-600 text-sm">
                Drawing from traditional Indian motifs and contemporary design trends to create unique concepts.
              </p>
            </div>

            <div className="text-center stagger-item" style={{ animationDelay: '0.2s' }}>
              <div className="w-20 h-20 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-4 scale-in">
                <span className="text-2xl font-bold text-black">2</span>
              </div>
              <h3 className="text-lg font-semibold text-black mb-2">Craft</h3>
              <p className="text-gray-600 text-sm">
                Skilled artisans handcraft each piece using traditional techniques combined with modern precision.
              </p>
            </div>

            <div className="text-center stagger-item" style={{ animationDelay: '0.3s' }}>
              <div className="w-20 h-20 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-4 scale-in">
                <span className="text-2xl font-bold text-black">3</span>
              </div>
              <h3 className="text-lg font-semibold text-black mb-2">Polish</h3>
              <p className="text-gray-600 text-sm">
                Every detail is carefully refined to achieve the perfect finish that honors both tradition and modernity.
              </p>
            </div>

            <div className="text-center stagger-item" style={{ animationDelay: '0.4s' }}>
              <div className="w-20 h-20 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-4 scale-in">
                <span className="text-2xl font-bold text-black">4</span>
              </div>
              <h3 className="text-lg font-semibold text-black mb-2">Deliver</h3>
              <p className="text-gray-600 text-sm">
                Your piece is carefully packaged and delivered, ready to become part of your story.
              </p>
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="mb-20">
          <h2 className="text-3xl font-elegant font-bold text-black text-center mb-12 fade-in-up">Meet Our Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-amber-100 rounded-xl p-6 border border-amber-200 text-center hover-lift stagger-item" style={{ animationDelay: '0.1s' }}>
              <div className="w-24 h-24 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-4 scale-in">
                <svg className="w-12 h-12 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-black mb-2">Gautham Talloju</h3>
              <p className="text-amber-700 font-medium mb-2">Founder & Creative Director</p>
              <p className="text-gray-600 text-sm">
                A passionate jewellery designer from a traditional Indian family, bringing 15+ years of 
                experience in creating pieces that bridge heritage and contemporary style.
              </p>
            </div>

            <div className="bg-amber-100 rounded-xl p-6 border border-amber-200 text-center hover-lift stagger-item" style={{ animationDelay: '0.2s' }}>
              <div className="w-24 h-24 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-4 scale-in">
                <svg className="w-12 h-12 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-black mb-2">Priya Sharma</h3>
              <p className="text-amber-700 font-medium mb-2">Master Craftsman</p>
              <p className="text-gray-600 text-sm">
                With 20 years of experience in traditional Indian jewellery making, Priya brings 
                unparalleled skill in traditional techniques and modern innovation.
              </p>
            </div>

            <div className="bg-amber-100 rounded-xl p-6 border border-amber-200 text-center hover-lift stagger-item" style={{ animationDelay: '0.3s' }}>
              <div className="w-24 h-24 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-4 scale-in">
                <svg className="w-12 h-12 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-black mb-2">Arjun Patel</h3>
              <p className="text-amber-700 font-medium mb-2">Customer Experience</p>
              <p className="text-gray-600 text-sm">
                Dedicated to ensuring every customer finds the perfect piece that honors their heritage 
                while fitting their modern lifestyle and personal style.
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center bg-amber-100 rounded-2xl p-12 border border-amber-200 hover-lift fade-in-up">
          <h2 className="text-3xl font-elegant font-bold text-black mb-4">
            Join Our Story
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Discover the perfect piece that bridges your heritage with contemporary style. Each Svaryn creation 
            is more than just jewellery – it's a testament to the beautiful fusion of traditional Indian 
            craftsmanship and modern elegance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-black hover:bg-gray-800 text-white font-medium py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl hover-lift">
              Shop Collection
            </button>
            <button className="bg-transparent border-2 border-black text-black hover:bg-black hover:text-white font-medium py-3 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover-lift">
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
